import {
    claimsModalEditingModes,
    claimsModalCompletionCodes,
} from '../claimsModal/claimsModal.constants';
import { castToBoolean } from '../../../../../lib/util';

const viewEventDetailsAdminPath = '/events/summary.php?ev_id=';

class PositionDisplayBox {
  constructor(
    $scope,
    $q,
    $timeout,
    $window,
    positionDetailsModal,
    APIService,
    StaffUserService,
    ClientService,
    RecapResponseService,
    PartialClaimService,
    DataSyncService,
    moment,
    geolocation,
    showClaimsModal,
    TaskQueueService,
    LogService,
    DateTimeService,
    DEBUG
  ) {
    "ngInject";
    this.$scope               = $scope;
    this.$q                   = $q;
    this.$timeout             = $timeout;
    this.$window              = $window;
    this.APIService           = APIService;
    this.StaffUserService     = StaffUserService;
    this.ClientService        = ClientService;
    this.RecapResponseService = RecapResponseService;
    this.PartialClaimService  = PartialClaimService;
    this.DataSyncService      = DataSyncService;
    this.moment               = moment;
    this.geolocation          = geolocation;
    this.geoError             = false;
    this.showClaimsModal      = showClaimsModal;
    this.TaskQueueService     = TaskQueueService;
    this.LogService           = LogService;
    this.DateTimeService      = DateTimeService;
    this.DEBUG                = DEBUG;
    this._isCommunicating     = false;
    this._isCommunicatingCheckin     = false;
    this._isBooking           = false;

    this.positionDetailsModal = positionDetailsModal;
    this._client              = null;

    this._constructed   = true;
    this._partialClaims = null;

    // CORE1-8103 Non-Compliant modal state (staff portal).
    this.ncModalOpen  = false;
    this.ncReasonId   = '';
    this.ncNotes      = '';
    this.ncSubmitting = false;
  }

  $onInit() {
    this._updatePartialClaimsForPosition();

    this.$scope.$on('error', (event, params) => {
        this.geoError = params.message;
    });

    this.$scope.$on('claimsModalClosed', (event, params) => {
      const positionId     = params && params.positionId;
      if (this.position && String(this.position.id) === String(positionId)) {
        this._updatePartialClaimsForPosition();
      }
    })
  }

  set isOnline(val) {
    this._isOnline = val;
  }

  get isOnline() {
    if (this.APIService) {
      return this.APIService.isOnline;
    }
    return false;
  }

  set isCommunicating(val) {
    this._isCommunicating = val;
  }

  get isCommunicating() {
    return this._isCommunicating;
  }

  set isCommunicatingCheckin(val) {
    this._isCommunicatingCheckin = val;
  }

  get isCommunicatingCheckin() {
    return this._isCommunicatingCheckin;
  }

  get debug() {
    return this.DEBUG;
  }

  set position(position) {
    this._position = position;
    this._updatePartialClaimsForPosition();
  }

  get position() {
    return this._position;
  }

  get positionStartTimeInEventTimezone() {
    const startTime         = this.position && this.position.start_time;
    const eventTimezoneName = this.position && this.position.event && this.position.event.timezoneName;
    return this.DateTimeService.parseMomentWithTimezone(startTime, eventTimezoneName);
  }

  get maconomyBrand() {
    if (!this.position.maconomy_enabled) {
      return false
    }
    return this.position?.event?.maconomy_brand
  }

  get positionEndTimeInEventTimezone() {
    const endTime           = this.position && this.position.end_time;
    const eventTimezoneName = this.position && this.position.event && this.position.event.timezoneName;
    return this.DateTimeService.parseMomentWithTimezone(endTime, eventTimezoneName);
  }

  get hasExpirationDate() {
    return !!(this.position && this.position.event && this.position.event.expiration_date);
  }

  get expirationDateInEventTimezone() {
    const expirationDate    = this.position && this.position.event && this.position.event.expiration_date;
    const eventTimezoneName = this.position && this.position.event && this.position.event.timezoneName;
    return this.DateTimeService.parseMomentWithTimezone(expirationDate, eventTimezoneName);
  }

  get daysUntilExpiration() {
    const expirationDate    = this.position && this.position.event && this.position.event.expiration_date;
    const eventTimezoneName = this.position && this.position.event && this.position.event.timezoneName;
    return this.DateTimeService.daysUntil(expirationDate, eventTimezoneName);
  }

  /**
   * Expiration urgency bucket for the days-remaining indicator (CORE1-7840).
   * daysUntilExpiration is clamped at 0, so 0/overdue lands in 'danger'.
   *   <= 2 days  -> danger (red)
   *   3-5 days   -> warning (amber)
   *   >= 6 days  -> success (green)  (covers the ticket's 6-10 and beyond)
   * Returns null when there's no expiration date, so colors only apply then.
   *
   * @returns {('danger'|'warning'|'success'|null)}
   */
  get expirationSeverity() {
    if (!this.hasExpirationDate) return null;
    // Finished (approved) activities get no urgency coloring at all.
    const ev = this.position && this.position.event;
    if (ev && ev.events_statuses_definition === 'STATUS_APPROVED') return null;
    const days = this.daysUntilExpiration;
    if (days === null || days === undefined) return null;
    if (days <= 2) return 'danger';
    if (days <= 5) return 'warning';
    return 'success';
  }

  /** Bootstrap contextual text class for the days-remaining label, or ''. */
  get expirationTextClass() {
    const sev = this.expirationSeverity;
    return sev ? ('text-' + sev) : '';
  }

  /**
   * Inline left-accent border style for the card, keyed to expiration urgency.
   * Inline (rather than a CSS class) because the card's global stylesheet is
   * built/served by a separate pipeline from this bundle - this keeps the
   * accent self-contained in the staff build. Empty object => no accent.
   *
   * @returns {object}
   */
  get expirationAccentStyle() {
    const colors = { danger: '#d9534f', warning: '#f0ad4e', success: '#5cb85c' };
    const sev = this.expirationSeverity;
    return sev ? { 'border-left': '4px solid ' + colors[sev] } : {};
  }

  get showRecap() {
    return this.position.canRecap && !this.showEditRecap && !this.showContinueRecap;
  }

  get showEditRecap() {
    return (this.position.hasRecap || this.position.enqueuedRecapResponse) && this.position.canRecap && !this.showContinueRecap;
  }

  // If there's a partial recap show 'Continue Recap'
  get showContinueRecap() {
    const eventId = this.position && this.position.event && this.position.event.id;
    if (eventId) {
      const partialRecaps = this.staffUser && this.staffUser.getPartialRecapsForEventId(eventId);
      return partialRecaps.length > 0;
    }
    return false;
  }

  get usePromptRecapCheckoutClaim() {
    return this.position && this.position.event && castToBoolean(this.position.event.prompt_recap_checkout_claim)
  }

  // position.recapRejected, not position.isRejected: Position exposes no isRejected getter -
  // isRejected is a property of the recap RESPONSE, which Position.recapRejected reads on the way
  // to its event-row fallback. Reading it off the position returned undefined for every shift,
  // rejected or not. Nothing consumes this getter today, so the correction is not reachable
  // behaviour; it is made so the next caller does not inherit a getter that cannot report true.
  get recapRejected() {
    return this.position && this.position.recapRejected;
  }

  /**
   * The reason the office gave for rejecting THIS staff member's recap. Null when the shift is
   * not rejected, and null again when it is rejected with no reason given - which is ordinary,
   * because both decline endpoints default the reason to null.
   *
   * This getter gates only the nested reason element, NOT the banner. The banner keys on
   * position.recapRejected (positionDisplayBox.html:13) and must keep doing so: collapsing the
   * two conditions into a single ng-if on this getter would render nothing at all for a
   * reasonless decline, which is the defect GH-4058 exists to fix.
   *
   * Scoped the same way as Position.recapRejected: the staffer's own recap response wins whenever
   * it is present, and the shared event row stands in only when that response never reached the
   * client. Reading the event row while a response is present would risk showing this staffer the
   * office's note about somebody else's recap.
   */
  get declinedRecapReason() {
    if (!this.position || !this.position.recapRejected) {
      return null;
    }
    const reason = this.position.recapResponse
      ? this.position.recapResponse.declined_reason
      : (this.position.event && this.position.event.recap_rejected_message);

    // Both arms can arrive as something other than a string. db::unpack() json-decodes any
    // stored value that parses as an object or array, and no reader on either path passes
    // $ignore_unpack, so an office decline reason that happens to be valid JSON - '{"note":"redo
    // the photos"}' - reaches the client decoded. That is still a reason the office typed, and a
    // staffer shown nothing cannot act on it, so it is serialised back rather than dropped: the
    // text is close to what was typed, and Angular interpolation escapes it either way. Only an
    // absent, empty or unserialisable value falls through to the reasonless banner the template
    // already renders.
    if (typeof reason === 'string') {
      return reason !== '' ? reason : null;
    }
    if (reason === null || reason === undefined) {
      return null;
    }
    try {
      const serialized = JSON.stringify(reason);
      // JSON.stringify() returns undefined for a value it cannot represent at all, and a
      // circular structure throws; both mean there is no text to show.
      return typeof serialized === 'string' && serialized !== '' ? serialized : null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Whether THIS staffer's own recap response is the rejected one, as opposed to the shift merely
   * sitting on an event whose shared row is flagged.
   *
   * This is what the two ng-disabled expressions that let a rejection override the mandatory
   * check-in lock have to read. Position.recapRejected falls back to the event row when the
   * staffer's own response never reached the client, and an event row can be flagged by the
   * legacy office reject path for an event with several staffers on it - so reading it there
   * would unlock Edit Recap and Add Photos for staffers whose own recap nobody rejected, and
   * before the check-in they are locked pending. The banner and the list bucketing do want the
   * fallback; the lock override does not.
   *
   * So when the response never reached the client the banner renders and the shift stays locked
   * behind the mandatory check-in, with no override. That is not new: develop's override read
   * position.recapRejected while THAT getter was response-only, so an absent response has always
   * left the controls locked. Widening the override to the event row would not fix it either - it
   * would unlock Edit Recap and Add Photos on every multi-staffer event whose shared row carries
   * somebody else's decline. Reaching the response itself is what the fallback case needs, and
   * that means loading outside the sync window, which nothing here does.
   */
  get ownRecapRejected() {
    return Boolean(
      this.position
      && this.position.recapResponse
      && this.position.recapResponse.isRejected
    );
  }

  get showSubmitClaims() {
    if (!this.claimsAllowed) {
      return false;
    }
    if (this.position.canSubmitClaims && this.usePromptRecapCheckoutClaim) {
      if (this.showEditClaims) return false
      if (this.showContinueRecap) return false
      return !!(this.position.hasRecap || this.position.enqueuedRecapResponse) && this.position.isCheckedOut
    }
    return !this.showEditClaims && !this.showContinueClaims && this.position.canSubmitClaims && (!this.usePromptRecapCheckoutClaim || this.position.isCheckedOut )
  }

  get claimsAllowed() {
    return this.position.allow_claims;
  }

  get showEditClaims() {
    return this.position && !this.showContinueClaims && this.position.staffUserCanEditClaims(this.staffUser);
  }

  get showContinueClaims() {
    return this.position && this.position.canSubmitClaims && !!this._partialClaims;
  }

  get showAddGuerillaButton() {
    return this.position && this.position.canAddGuerillaStop;
  }

  get addGuerillaButtonDisabled() {
    return this.showAddGuerillaButton && !this.isOnline;
  }

  get eventId() {
    return this.position && this.position.events_id;
  }

  get clientInfo() {
    if (this._clientInfo === undefined) {
      let clientId   = this.position && this.position.clients_id;
      this._clientInfo = `${clientId}`;
      if (clientId) {
        this.ClientService.getById(clientId)
          .then(client => {
            if (client) {
              this._clientInfo = `${client.name} (host: ${client.host_prefix} id: ${client.id})`;
            } else {
              this.LogService.warn("Unable to load client with id", clientId);
            }
          })
      }
    }
    return this._clientInfo;
  }

  get client() {
    return this._client;
  }

  /**
   * When the position's client hides ad-hoc event times (staff_adhoc_hide_times),
   * the time on the shift card is meaningless (defaulted at creation), so the
   * card suppresses the time row. Display-only.
   *
   * @returns {boolean}
   */
  get hideEventTimes() {
    return !!(this._client && this._client.staff_adhoc_hide_times);
  }

  get clientId() {
    return this.position && this.position.clients_id;
  }

  get clientName() {
    return this.client && this.client.name;
  }

  get clientHostPrefix() {
    return this.client && this.client.host_prefix;
  }

  get viewEventDetailsAdminURL() {
    const hostPrefix = this.clientHostPrefix;
    const eventId = this.eventId;
    if (!hostPrefix || !eventId) {
      return null;
    }
    const path = viewEventDetailsAdminPath + eventId;
    return this.APIService.convertPathToURLWithHostPrefix(path, hostPrefix);
  }

  get recapDefinitionInfo() {
    if (this.position && this.position.event && this.position.event.events_recaps_id) {
      return this.position.event.events_recaps_id;
    }
    return null;
  }

  disableShiftActions() {
    if (this.lockForMandatoryCheckIn) {
      return true;
    }
    return false;
  }

  checkOtherPositionLoggedIn() {
    console.log('checkOtherPositionLoggedIn', this.otherPositionCheckedIn);
    return this.otherPositionCheckedIn;
  }

  showRecapResponse() {
    this.$scope.$emit('showRecapResponseForPosition', this.position);
  }

  showAddGuerilla() {
    this.$scope.$emit('showAddGuerilla', this.position);
  }

  hasFilesToShow() {
    const posDocs  = (this.position && this.position.documents) || [];
    const progDocs = (this.position && this.position.event && this.position.event.program_documents) || [];
    return posDocs.length > 0 || progDocs.length > 0;
  }

  showFiles() {
    this.$scope.$emit('showFiles', this.position);
  }

  showAddPhotos() {
    this.$scope.$emit('showAddPhotos', this.position);
  }

  canAddPhotos() {
    return !this.position.is_eds && !this.position.disable_add_photos;
  }

  hasEventProducts() {
    return this.position && this.position.event
        && this.position.event.has_products
        && this.position.event.event_products
        && this.position.event.event_products.length > 0;
  }

  // Per-client "Show Products Button on Shifts" toggle (the client's Field
  // Staff Settings). Off by default. Resolved from the logged-in user's synced
  // clients keyed by the position's client - the same source the ad-hoc modal
  // uses. (The ClientService 'Client' data-sync store is not reliably
  // populated, so we deliberately don't depend on it here.)
  get showProductsButton() {
    const user     = this.StaffUserService && this.StaffUserService.currentUser;
    const clients  = (user && user.clients) || [];
    const clientId = this.position && this.position.clients_id;
    const client   = clients.find(c => parseInt(c.id, 10) === parseInt(clientId, 10));
    return !!(client && client.staffing_show_products_button);
  }

  showProducts() {
    this.$scope.$emit('showProductsForPosition', this.position);
  }

  // CORE1-8103 Non-Compliance (staff portal). The per-position flags are set
  // server-side in PositionController::index: non_compliance_enabled is true
  // only for merchandising visits when the workflow is on; non_compliant is the
  // current mark state driving the button/label toggle.
  get showNonCompliantButton() {
    return !!(this.position && this.position.non_compliance_enabled);
  }

  get isNonCompliant() {
    return !!(this.position && this.position.non_compliant);
  }

  get nonComplianceReasons() {
    return (this.staffUser && this.staffUser.non_compliance_reasons) || [];
  }

  openNonCompliantModal() {
    this.ncReasonId  = '';
    this.ncNotes     = '';
    this.ncModalOpen = true;
  }

  closeNonCompliantModal() {
    this.ncModalOpen = false;
  }

  submitNonCompliant() {
    if (this.ncSubmitting) {
      return;
    }
    // Reason is required when the client has configured reasons.
    if (this.nonComplianceReasons.length && !this.ncReasonId) {
      this.$window.alert('Please select a reason.');
      return;
    }
    this.ncSubmitting = true;
    const staffUserId = this.StaffUserService.currentUser.id;
    const positionId  = this.position.id;
    const url = `/staffing_user/${staffUserId}/positions/${positionId}/non_compliant`;

    this.APIService.post(url, { non_compliance_reasons_id: this.ncReasonId, notes: this.ncNotes })
      .then(() => {
        this.position.non_compliant = true;
        this.ncModalOpen = false;
        this.LogService.log(`Marked position ${positionId} non-compliant`);
        this.DataSyncService.sync();
      })
      .catch(err => {
        const message = (err && err.data && err.data.message) || 'Unable to mark this visit non-compliant. Please try again.';
        this.$window.alert(message);
        this.LogService.error(`Mark non-compliant failed for position ${positionId}:`, err);
      })
      .finally(() => {
        this.ncSubmitting = false;
      });
  }

  markCompliant() {
    if (this.ncSubmitting) {
      return;
    }
    if (!this.$window.confirm('Clear the non-compliant mark on this visit?')) {
      return;
    }
    this.ncSubmitting = true;
    const staffUserId = this.StaffUserService.currentUser.id;
    const positionId  = this.position.id;
    const url = `/staffing_user/${staffUserId}/positions/${positionId}/compliant`;

    this.APIService.post(url, {})
      .then(() => {
        this.position.non_compliant = false;
        this.LogService.log(`Marked position ${positionId} compliant`);
        this.DataSyncService.sync();
      })
      .catch(err => {
        const message = (err && err.data && err.data.message) || 'Unable to clear the non-compliant mark. Please try again.';
        this.$window.alert(message);
        this.LogService.error(`Mark compliant failed for position ${positionId}:`, err);
      })
      .finally(() => {
        this.ncSubmitting = false;
      });
  }

  disableEditPosition() {
    if (!castToBoolean(this.position.event.prompt_recap_checkout_claim)) {
      return false
    }

    const hasResponse = !!this.position.recapResponse

    if (!hasResponse) {
      return false
    }

    return true
  }

  showEditPosition() {
    this.$scope.$emit('showEditPosition', this.position);
  }

  get recapCompletePromptRecapCheckoutClaim() {
    if (!castToBoolean(this.position.event.prompt_recap_checkout_claim)) {
      return false
    }

    const checkedIn = this.position && this.position.checkin && this.position.checkin.length && this.position.checkin[this.position.checkin.length - 1 ].type == 'in'
    const hasResponse = !!this.position.recapResponse
    if (checkedIn) {
      if (hasResponse) {
        return false
      }
      return true
    }

    return this.position && hasResponse
  }

  shouldAllowCheckinButton() {
      return !!(
        castToBoolean(this.allowCheckin)
        && !castToBoolean(this.isCommunicating)
        && castToBoolean(this.position.allow_event_checkin)
        && this.position.isCurrent
        && !this.positionRequiringClaim
        && !this.recapCompletePromptRecapCheckoutClaim
      );
  }

  displayDetails(position) {
    return this.positionDetailsModal({
      position: position,
      modalClass: 'visits-modal',
    });
  }

  checkInOut(position, direction) {
    this._isCommunicatingCheckin = true;
    let locationPromise = this.geolocation.getLocation()
      .catch(() => {
        alert('Geolocation is unavailable. Please enable location access.');
        this._isCommunicating = false;
        this.LogService.log(`Geolocation promise fail - setting 0,0 coordinates (event=${position.real_events_id})`);
        return {
            coords: {
                latitude: 0,
                longitude: 0,
            }
        };
      });
    return locationPromise
      .then(locationData => {
        const task = this.TaskQueueService.createTask('CheckinCheckoutTask');
        task.lat = locationData.coords.latitude;
        task.lon = locationData.coords.longitude;

        this._isCommunicating = false;
        this.LogService.log(task.lat, task.lon);

        const timestamp = this.moment().format();

        task.staffUserId = this.StaffUserService.currentUser.id;
        task.timestamp   = timestamp;
        task.direction   = direction;
        task.eventId     = position.real_events_id;

        // Creating a local checkin/out. Typically, these are returned on sync.
        // This is needed because we need the actual checkin data to populate
        // in the claim editor.
        const localCheckin = {
          timestamp,
          type: direction,
          events_id: position.real_events_id,
        };

        if (task.canExecute) {
          return task.execute()
            .then(result => {
              position.toggleCheckinStatus();
              position.pushLocalCheckin(localCheckin);
              this._isCommunicatingCheckin = false;
              return result;
            })
            .catch(err => {
              this.LogService.error(`Error executing checkin/out task (event=${position.real_events_id}):`, err);
              return this.$q.reject(err);
            });
        }
        else {
          // Enqueue task for later execution
          return this.TaskQueueService.enqueue(task)
            .then(() => {
              this.LogService.debug(`Checkin/out task enqueued; toggling checkin status (event=${position.real_events_id})`);
              this._isCommunicatingCheckin = false
              position.toggleCheckinStatus();
              position.pushLocalCheckin(localCheckin);
            });
        }
      }).catch(() => {
        this._isCommunicatingCheckin = false;
      });
  }

  checkIn(position) {
      this.checkInOut(position, 'in');
  }

  checkOut(position) {
      this.checkInOut(position, 'out');
  }

  get isBooking() {
    return this._isBooking;
  }

  /**
   * True when the app has network connectivity (device online and not in
   * manual offline mode). The Book action is hidden when false, since we
   * cannot reach the server to claim the position.
   */
  get isOnline() {
    return !!(this.APIService && this.APIService.isOnline);
  }

  /**
   * Self-book the current staff user into this available position.
   * Shows consent dialog if booking_consent text is configured, then calls the booking API.
   */
  bookPosition() {
    const consentText = this.staffUser && this.staffUser.booking_consent;
    if (consentText) {
      if (!this.$window.confirm(consentText)) {
        return;
      }
    }

    this._isBooking = true;
    const staffUserId = this.StaffUserService.currentUser.id;
    const positionId = this.position.id;
    const url = `/staffing_user/${staffUserId}/positions/${positionId}/book`;

    this.APIService.post(url, {})
      .then(() => {
        this.LogService.log(`Successfully self-booked position ${positionId}`);
        this.$scope.$emit('selfBookingComplete', { positionId });
        this.DataSyncService.sync();
      })
      .catch(err => {
        const errorCode = err && err.data && err.data.error;
        const message = (err && err.data && err.data.message) || 'Unable to book this position. Please try again.';
        this.$window.alert(message);
        this.LogService.error(`Self-booking failed for position ${positionId}:`, err);
        // Race lost to another staff user. Refresh the list so the stale
        // position drops out of the available view immediately.
        if (errorCode === 'no_longer_available') {
          this.$scope.$emit('selfBookingUnavailable', { positionId });
          this.DataSyncService.sync();
        }
      })
      .finally(() => {
        this._isBooking = false;
      });
  }

  editClaims() {
    return this.openClaims({editingMode: claimsModalEditingModes.editPending})
      .then(result => this._checkSubmitClaimsResult(result));
  }

  continueClaims() {
    return this.openClaims({editingMode: claimsModalEditingModes.continuingClaim})
      .then(result => this._checkSubmitClaimsResult(result));
  }

  submitClaims() {
    return this.openClaims({editingMode: claimsModalEditingModes.new})
      .then(result => {
        return this._checkSubmitClaimsResult(result);
      });
  }

  /**
   * Post-processing for claim submission.
   * Checks to see if recap should be auto-opened
   */
  _checkSubmitClaimsResult(submitClaimsResult) {
    const position              = this.position;
    const event                 = position && position.event;
    const recapClaimLinkEnabled = event && castToBoolean(event.prompt_recap_claim);
    const recapButtonEnabled    = this.showRecap;
    const claimSubmitted        = submitClaimsResult === claimsModalCompletionCodes.submitted;

    /**
     * Determine whether we should automatically show recap response, based on the "prompt_recap_claim" setting on
     * the event. In order to enable this feature, the setting must be true, and there must be no existing recap
     * response for this position.
     */
    if (claimSubmitted && recapClaimLinkEnabled && recapButtonEnabled) {
      // Recap button is enabled (showRecap == true); this means there is no existing recap response, and we should
      // collect one
      return this.showRecapResponse();
    }
    return submitClaimsResult;
  }

  openClaims(props) {
    props            = props || {};
    props.modalClass = 'visits-modal';
    if (!props.position) {
      props.position = this.position;
    }
    if (!props.event) {
      props.event = this.position && this.position.event;
    }
    if (!props.staffUser) {
      props.staffUser = this.staffUser;
    }

    return this.showClaimsModal({
      ...props,
      usePromptRecapCheckoutClaim: this.usePromptRecapCheckoutClaim
    })
      .then(result => {
        this._updatePartialClaimsForPosition();
        return result;
      })
      .catch(err => {
        this.LogService.error(`PositionDisplayBox openClaims finished with error`, err);
        this._updatePartialClaimsForPosition();
      })
  }

  _updateClientForCurrentPosition() {
    if (!this._constructed) {
      return;
    }

    const clientId   = this.position && this.position.clients_id;
    const eventId    = this.position && this.position.events_id;
    const positionId = this.position && this.position.id;

    if (clientId && this.ClientService) {
      this.ClientService.getById(clientId)
        .then(client => {
          if (!client) {
            this.LogService.warn(`ClientService.getById(${clientId}) returned null; not updating client for event ${eventId} position ${positionId}`);
            return;
          }
          this.$timeout(() => {
            this._client = client;
          });
        })
        .catch(err => {
          this.LogService.warn(`Error retrieving client for event ${eventId} position ${positionId}:`, err);
          this._client = null;
        });
    } else {
      this.LogService.warn(`Unable to retreive client info for event ${eventId} position ${positionId}; missing clients id. Position:`, this.position);
      this._client = null;
    }
  }

  _updatePartialClaimsForPosition() {
    if (!this.$q) {
      return;
    }
    return this.$q.when(this.PartialClaimService.getForPosition(this.staffUser, this.position))
      .then(partialClaims => {
        this._partialClaims = partialClaims;
      })
      .catch(err => {
        this.LogService.error(`Error retrieving partial claims for position:`, err);
        return this.$q.reject(err);
      });
  }
}

export default PositionDisplayBox;

